import { useState, useEffect } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { UPDATE_EVENT, isUpdateAvailable } from '@/lib/version-check';

/**
 * Non-blocking banner that appears when a new version is deployed.
 * Dismissible — user can close it and keep working.
 * Uses the existing Update.gif for visual consistency with the chunk-error page.
 */
export function UpdateBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Check if update was already detected before mount
    if (isUpdateAvailable()) {
      setShow(true);
      return;
    }

    const handleUpdate = () => setShow(true);
    window.addEventListener(UPDATE_EVENT, handleUpdate);
    return () => window.removeEventListener(UPDATE_EVENT, handleUpdate);
  }, []);

  if (!show) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[60] animate-in slide-in-from-top duration-300">
      <div className="bg-primary text-primary-foreground shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src="/images/pages/Update.gif"
              alt="Update available"
              className="h-8 w-8 rounded object-cover flex-shrink-0 hidden sm:block"
            />
            <div className="min-w-0">
              <p className="text-sm font-medium">
                JD Simplified was just updated
              </p>
              <p className="text-xs opacity-80 hidden sm:block">
                Refresh the page to get the latest features and improvements.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => window.location.reload()}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-foreground text-primary rounded-md text-sm font-medium hover:opacity-90 transition-opacity cursor-pointer"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </button>
            <button
              onClick={() => setShow(false)}
              className="p-1.5 rounded-md hover:bg-primary-foreground/20 transition-colors cursor-pointer"
              aria-label="Dismiss update notification"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
